import {header} from "./blocks/header";
import {list} from "./blocks/list";
import {paragraph} from "./blocks/paragraph";
import {parse} from "./parser";
import {filterBlock} from "./conditions";
import {extractGroupsAndVariablesIds, getConditionalGroupsAndVariablesIds} from "./extractor";

interface Template {
    id: string;
    metas: any;
    content: {
        sections: Section[];
        variablesGroups: VariablesGroup[];
    };
}

interface Section {
    id: string;
    blocks: (BaseBlock | ParagraphBlock | ListBlock | HeaderBlock)[];
}

interface BaseBlock {
    id: string;
    type: string;
    data: any;
}

interface Condition {
    groupId: string;
    variableId: string;
    operator: string;
}

interface HeaderBlock extends BaseBlock {
    type: "header";
    data: {
        level: number;
        text: string;
        groupId?: string;
        variableId?: string;
        type?: string;
        operator?: string;
        conditions?: Condition[]
    };
}

interface ParagraphBlock extends BaseBlock {
    type: "paragraph";
    data: {
        text: string;
        groupId?: string;
        variableId?: string;
        type?: string;
        operator?: string;
        conditions?: Condition []
    };
}

interface ListBlock extends BaseBlock {
    type: "list";
    data: {
        items: string[];
        style: "ordered" | "unordered";
        groupId?: string;
        variableId?: string;
        type?: string;
        blurred?: string;
        operator?: string;
        conditions?: Condition []
    };
}

interface VariablesGroup {
    id: string;
    variables: Variable[];
}

interface Variable {
    id: string;
    value?: string;
    example?: string;
}

interface Config {
    cssClass: string;
}

export function generate(
    template: Template,
    variablesGroup: VariablesGroup[],
    config: Config = {cssClass: ""},
) {
    // Generate a Map of pair : "groupId-variableId", {value: string, example: string}
    const userVariablesGroupMap = new Map(
        variablesGroup.flatMap((g) =>
            g.variables.filter((v) => !!v.value).map((v) => [`${g.id}-${v.id}`, v.value]),
        ),
    );

    const exampleVariablesGroupsMap = new Map(
        template.content.variablesGroups.flatMap((g) =>
            g.variables.map((v) => [`${g.id}-${v.id}`, v.example]),
        ),
    );

    const blocks = template.content.sections.flatMap((section) => section.blocks);

    return blocks
        .filter((block) => filterBlock(block, userVariablesGroupMap))
        .map((block) => {

            const blurred = block.data.blurred
            const alignment = block.data.alignment

            switch (block.type) {
                case "paragraph":
                    return paragraph((block as ParagraphBlock).data.text, blurred, alignment);

                case "list":
                    return list((block as ListBlock).data, blurred);

                case "header":
                    return header((block as HeaderBlock).data, blurred);

                default:
                    return "";
            }
        })
        .filter((content) => content !== "")
        .map((content) =>
            parse(content, userVariablesGroupMap, exampleVariablesGroupsMap, config.cssClass),
        )
        .join("\n");
}

export function getDisplayedGroupsAndVariableIds(
    template: Template,
    variablesGroup: VariablesGroup[]
) {
    // Generate a Map of pair : "groupId-variableId", {value: string, example: string}
    const userVariablesGroupMap = new Map(
        variablesGroup.flatMap((g) =>
            g.variables.filter((v) => !!v.value).map((v) => [`${g.id}-${v.id}`, v.value]),
        ),
    );

    const blocks = template.content.sections.flatMap((section) => section.blocks);

    let variablesIds = []
    let groupsIds = []

    blocks.forEach(block => {
        if (block.data.type == "conditionalComplex") {
            const {conditionalVariablesIds, conditionalGroupsIds} = getConditionalGroupsAndVariablesIds(block.data)
            variablesIds.push(...conditionalVariablesIds)
            groupsIds.push(...conditionalGroupsIds)
        }
    })

    const variablesAndGroupsFromClocks = blocks
        .filter((block) => filterBlock(block, userVariablesGroupMap))
        .reduce((aggr, block) => {
            if (block.data.text) {
                const {variablesIds, groupsIds} = extractGroupsAndVariablesIds(block.data.text)
                const allVariablesIds = [...aggr.variablesIds, ...variablesIds]
                const allGroupsIds = [...aggr.groupsIds, ...groupsIds]
                aggr.variablesIds = Array.from(new Set(allVariablesIds))
                aggr.groupsIds = Array.from(new Set(allGroupsIds))
            }
            return aggr
        }, {variablesIds: [], groupsIds: []})

    variablesIds.push(...variablesAndGroupsFromClocks.variablesIds)
    groupsIds.push(...variablesAndGroupsFromClocks.groupsIds)

    variablesIds = Array.from(new Set(variablesIds))
    groupsIds = Array.from(new Set(groupsIds))

    return {
        variablesIds,
        groupsIds
    }
}
